import LanguageDetector from 'i18next-browser-languagedetector';
import { useState } from 'react';
import { I18nextProvider } from 'react-i18next';

import {
  createI18nInstance,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@/i18n';

const storageKey = 'i18nextLogtoUiLng';

export const detectLanguage = (): string | undefined => {
  const languageDetector = new LanguageDetector();
  languageDetector.init(
    { languageUtils: {} },
    {
      lookupLocalStorage: storageKey,
      lookupSessionStorage: storageKey,
    }
  );

  const detected = languageDetector.detect();

  if (Array.isArray(detected)) {
    return detected[0];
  }

  return typeof detected === 'string' ? detected.replace('-US', '') : detected;
};

const langStorageKey = 'onemember_lang';

const supportedLocaleSet: ReadonlySet<string> = new Set(SUPPORTED_LOCALES);

const isSupportedLocale = (value: string): value is SupportedLocale =>
  supportedLocaleSet.has(value);

/**
 * Read `lang` query param from the URL and return it if it's a supported locale.
 * Persists to sessionStorage so language survives auth redirects.
 * Cleans the param from the URL after reading.
 *
 * Note: handleSearchParametersData() in App.tsx may run before this (at module-import time),
 * which stores `lang` to sessionStorage under key 'lang' and cleans it from the URL.
 * In that case getLanguageFromUrl() won't find it — getLanguageFromStorage() handles this.
 */
const getLanguageFromUrl = (): SupportedLocale | undefined => {
  try {
    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');

    if (lang && isSupportedLocale(lang)) {
      // Persist to sessionStorage so language survives auth redirects
      sessionStorage.setItem(langStorageKey, lang);

      // Clean lang param from URL
      params.delete('lang');
      const newUrl =
        params.size > 0
          ? `${window.location.pathname}?${params.toString()}`
          : window.location.pathname;
      window.history.replaceState(null, '', newUrl);

      return lang;
    }
  } catch {
    // Ignore errors
  }

  return undefined;
};

/**
 * Read previously stored language from sessionStorage.
 * Checks our key, the 'lang' key, and the 'ui_locales' key
 * (both set by handleSearchParametersData in App.tsx).
 * ui_locales may be space-separated (OIDC spec), so we take the first value.
 */
const getLanguageFromStorage = (): SupportedLocale | undefined => {
  try {
    const lang = sessionStorage.getItem(langStorageKey) ?? sessionStorage.getItem('lang');

    if (lang && isSupportedLocale(lang)) {
      return lang;
    }

    // Check ui_locales (OIDC standard param, may be space-separated e.g. "en th")
    const uiLocales = sessionStorage.getItem('ui_locales');
    if (uiLocales) {
      const firstLocale = uiLocales.split(' ')[0];
      if (firstLocale && isSupportedLocale(firstLocale)) {
        return firstLocale;
      }
    }
  } catch {
    // Ignore errors
  }

  return undefined;
};

type I18nProviderProps = {
  readonly children: React.ReactNode;
};

export const I18nProvider = ({ children }: I18nProviderProps) => {
  const [i18n, setI18n] = useState(() => {
    const instance = createI18nInstance();
    const locale = getLanguageFromUrl() ?? getLanguageFromStorage() ?? DEFAULT_LOCALE;
    void instance.changeLanguage(locale);
    instance.dir('ltr');
    return instance;
  });
  void setI18n;

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};
